import { apiGetRequest, apiPutRequest } from "helpers/Requests";

export const PARTICIPANT_LISTINGS_PER_PAGE_KEY = "participant_listings_per_page";
export const PARTICIPANT_LISTINGS_PER_PAGE_OPTIONS = [10, 25, 50];
export const DEFAULT_PARTICIPANT_LISTINGS_PER_PAGE = 10;

const CACHE_PREFIX = "v360_ui_pref_participant_listings_per_page";

export const PARTICIPANT_LISTINGS_PER_PAGE_SELECT_OPTIONS =
  PARTICIPANT_LISTINGS_PER_PAGE_OPTIONS.map((value) => ({
    label: String(value),
    value,
  }));

export function normalizeParticipantListingsPerPage(value) {
  const n = Number(value);
  return PARTICIPANT_LISTINGS_PER_PAGE_OPTIONS.includes(n)
    ? n
    : DEFAULT_PARTICIPANT_LISTINGS_PER_PAGE;
}

function getCacheUserKey() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "anonymous";
    const user = JSON.parse(raw);
    return String(user?.id ?? user?.uuid ?? user?.email ?? "anonymous");
  } catch (_error) {
    return "anonymous";
  }
}

function getCacheKey() {
  return `${CACHE_PREFIX}:${getCacheUserKey()}`;
}

/** Sync read — used so list fetches never wait on the preference API. */
export function readCachedParticipantListingsPerPage() {
  try {
    const raw = localStorage.getItem(getCacheKey());
    if (raw == null) return null;
    return normalizeParticipantListingsPerPage(raw);
  } catch (_error) {
    return null;
  }
}

export function writeCachedParticipantListingsPerPage(perPage) {
  try {
    localStorage.setItem(
      getCacheKey(),
      String(normalizeParticipantListingsPerPage(perPage))
    );
  } catch (_error) {
    // Ignore quota / private-mode failures; server remains source of truth.
  }
}

/**
 * GET /user/ui-preferences — lightweight; failures fall back to cache then default.
 */
export async function fetchParticipantListingsPerPage() {
  try {
    const res = await apiGetRequest("user/ui-preferences");
    const preferences =
      res?.data?.preferences ?? res?.data?.data?.preferences ?? {};
    const size = normalizeParticipantListingsPerPage(
      preferences?.[PARTICIPANT_LISTINGS_PER_PAGE_KEY]
    );
    writeCachedParticipantListingsPerPage(size);
    return size;
  } catch (_error) {
    return (
      readCachedParticipantListingsPerPage() ??
      DEFAULT_PARTICIPANT_LISTINGS_PER_PAGE
    );
  }
}

/**
 * PUT /user/ui-preferences — fire-and-forget friendly; updates local cache first.
 */
export async function saveParticipantListingsPerPage(perPage) {
  const value = normalizeParticipantListingsPerPage(perPage);
  writeCachedParticipantListingsPerPage(value);
  try {
    await apiPutRequest("user/ui-preferences", {
      [PARTICIPANT_LISTINGS_PER_PAGE_KEY]: value,
    });
  } catch (_error) {
    // Prefer not blocking the UI if persistence fails.
  }
  return value;
}
